import { SubBrandName } from 'lib/settings/brands/index'
import {
  createContext,
  PropsWithChildren,
  useCallback,
  useContext,
  useEffect,
  useState,
} from 'react'
import { TripTypeInputProps } from '../../Form/TripTypeInput/TripTypeInput'
import {
  LocationInputAddressExtended,
  LocationInputContentLocation,
} from '../collapsibles/BookingWidgetLocation'

import { AutoCompleteItem } from 'lib/types/AutoCompleteInput'
import { AddressInputAddress } from '../../Form/AddressInput/AddressInput'

export interface BookingWidgetContextData {
  /**
   * VALUES
   */
  addresses?: (AddressInputAddress | null)[][]
  areAutoCompleteItemsLoading?: boolean[][]
  autoCompleteItems?: AutoCompleteItem[][][]
  brand?: SubBrandName
  expandedStep?: string
  isCruise?: TripTypeInputProps['isCruise']
  loadingServiceLevels?: boolean
  locations?: LocationInputContentLocation[]
  partner?: LocationInputAddressExtended
  partnerField?: string
  productOptions?: OrderProductLabel[]
  products?: OrderProductLabel[]
  services?: (ServiceOptionWithAdminLabels | undefined)[]
  totalPriceFormatted?: string
  tripType?: TripTypeInputProps['type']
  validateOnExpand?: string
  validationFieldsPerCollapsible?: Record<string, string[]>
  /**
   * METHODS
   */
  clearAll?: () => void
  getData?: () => Pick<
    BookingWidgetContextData,
    'tripType' | 'isCruise' | 'products' | 'services' | 'locations'
  >
  serviceOptions?: ServiceOptionWithAdminLabels[]
  onInputSearchTermHandlers?: ((text: string) => void)[][]
  onSelectAutoCompleteItemHandlers?: ((item: AutoCompleteItem) => void)[][]
  registerCollapsibleValidationFields?: (
    collapsibleId: string,
    fields: string[],
  ) => void
  setExpandedStep?: (step?: string) => void
  setIsCruise?: (isCruise: BookingWidgetContextData['isCruise']) => void
  setLocations?: (locations: BookingWidgetContextData['locations']) => void
  setProducts?: (products: BookingWidgetContextData['products']) => void
  setServices?: (services: BookingWidgetContextData['services']) => void
  setTripType?: (tripType: BookingWidgetContextData['tripType']) => void
}

export interface BookingWidgetProviderProps
  extends Pick<
      BookingWidgetContextData,
      | 'addresses'
      | 'areAutoCompleteItemsLoading'
      | 'autoCompleteItems'
      | 'brand'
      | 'loadingServiceLevels'
      | 'onInputSearchTermHandlers'
      | 'onSelectAutoCompleteItemHandlers'
      | 'partner'
      | 'totalPriceFormatted'
    >,
    PropsWithChildren {
  productOptions?: OrderProductLabel[]
  serviceOptions?: ServiceOptionWithAdminLabels[]
  handleData?: (
    data: Pick<
      BookingWidgetContextData,
      'tripType' | 'isCruise' | 'products' | 'services' | 'locations'
    >,
  ) => void
  computeTotal?: (
    data: ReturnType<Required<BookingWidgetContextData>['getData']>,
  ) => number
  /**
   * Partner's field on initiation
   */
  partnerField: 'origin' | 'destination'
}

export const BookingWidgetContext = createContext<BookingWidgetContextData>({})

export function BookingWidgetProvider({
  addresses,
  areAutoCompleteItemsLoading,
  autoCompleteItems,
  loadingServiceLevels,
  onInputSearchTermHandlers,
  onSelectAutoCompleteItemHandlers,
  brand,
  children,
  serviceOptions,
  handleData,
  partner,
  partnerField,
  productOptions,
  totalPriceFormatted,
}: BookingWidgetProviderProps) {
  const [tripType, setTripType] =
    useState<BookingWidgetContextData['tripType']>('roundtrip')
  const [isCruise, setIsCruise] =
    useState<BookingWidgetContextData['isCruise']>(false)
  const [locations, setLocations] = useState<
    BookingWidgetContextData['locations']
  >([])
  const [products, setProductsState] = useState<
    BookingWidgetContextData['products']
  >([])
  const [services, setServices] = useState<
    BookingWidgetContextData['services']
  >([])
  const [data, setData] = useState<
    ReturnType<Required<BookingWidgetContextData>['getData']>
  >({})
  const [expandedStep, setExpandedStep] = useState<string>()
  const [validateOnExpand, setValidateOnExpand] = useState('')
  const [validationFieldsPerCollapsible, setValidationFieldsPerCollapsible] =
    useState<
      Required<BookingWidgetContextData>['validationFieldsPerCollapsible']
    >({})

  const setProducts = useCallback(
    (products: BookingWidgetContextData['products']) => {
      handleData?.({
        ...data,
        products,
      })
      setProductsState(products)
    },
    [data, handleData, setProductsState],
  )

  // Update number of origin-destination input based on tripType
  const updateTripType = useCallback<
    Required<BookingWidgetContextData>['setTripType']
  >(
    (type) => {
      if (tripType !== type) {
        setTripType(type)

        const newServices = services?.length ? services : []

        if (type === 'multi') {
          newServices.splice(1, 1, services?.[1])
        }

        setServices(newServices.filter(Boolean))
      }
    },
    [services, tripType],
  )

  const getData = useCallback(() => {
    return {
      tripType,
      isCruise,
      locations,
      products,
      services,
    }
  }, [tripType, isCruise, locations, products, services])

  const registerCollapsibleValidationFields = useCallback<
    Required<BookingWidgetContextData>['registerCollapsibleValidationFields']
  >(
    (id, fields) => {
      const registeredFields = validationFieldsPerCollapsible[id]

      // Check if fields are already registered for the collapsible
      if (
        registeredFields &&
        fields.length === registeredFields.length &&
        fields.every((fieldName) => registeredFields.includes(fieldName))
      )
        return

      validationFieldsPerCollapsible[id] = fields
      setValidationFieldsPerCollapsible(validationFieldsPerCollapsible)
    },
    [validationFieldsPerCollapsible],
  )

  const setCurrentExpandedStep = useCallback(
    (stepID?: string) => {
      if (stepID) {
        // Trip type should always be expandable
        if (stepID === 'tripType') {
          setExpandedStep('tripType')
          return
        }

        // Check whether locations were provided; if not, redirect to location
        if (stepID !== 'location') {
          const hasLocations = locations?.some(
            ({ origin, destination }) => origin || destination,
          )
            ? true
            : false

          if (!hasLocations) {
            setValidateOnExpand('location')
            setExpandedStep('location')
            return
          }
        }

        // Check whether products were added; if not, redirect to products
        if (stepID === 'service') {
          if (!products?.length) {
            setValidateOnExpand('product')
            setExpandedStep('product')
            return
          }
        }
      }

      setValidateOnExpand('')
      setExpandedStep(stepID)
    },
    [locations, products],
  )

  const clearAll = useCallback(() => {
    setTripType('roundtrip')
    setIsCruise(false)
    setLocations(undefined)
    setProducts([])
    setServices([])
    setExpandedStep(undefined)
  }, [setTripType, setIsCruise, setLocations, setProducts, setServices])

  useEffect(() => {
    setData(getData())
  }, [getData])

  useEffect(() => {
    handleData?.(data)
  }, [handleData, data])

  return (
    <BookingWidgetContext.Provider
      value={{
        // Values
        addresses,
        areAutoCompleteItemsLoading,
        autoCompleteItems,
        brand,
        expandedStep,
        isCruise,
        loadingServiceLevels,
        locations,
        partner,
        partnerField,
        productOptions,
        products,
        services,
        totalPriceFormatted,
        tripType,
        validateOnExpand,
        validationFieldsPerCollapsible,
        // Methods
        clearAll,
        getData,
        serviceOptions,
        onInputSearchTermHandlers,
        onSelectAutoCompleteItemHandlers,
        registerCollapsibleValidationFields,
        setExpandedStep: setCurrentExpandedStep,
        setIsCruise,
        setLocations,
        setProducts,
        setServices,
        setTripType: updateTripType,
      }}
    >
      {children}
    </BookingWidgetContext.Provider>
  )
}

export const useBookingWidgetStore = () => useContext(BookingWidgetContext)
